import { For, Show, type JSX } from "solid-js";

import { formatUSD } from "~/util/format";

import styles from "../deck.module.css";
import { useDeckWords } from "../deck_words";
import type { CostSlide as CostSlideData } from "../types";

// Past this many rows, the rows tighten so the total stays on the slide (the Composer folds any past eight).
const DENSE_ROWS = 5;

/** The plan's fees as its rows hold them: each treatment's fee, and with the insurance estimate on, what insurance may
 *  pay and the patient's part. Staff choose the columns and write the note; the numbers are never typed. */
export function CostSlide(props: { slide: CostSlideData }): JSX.Element {
  const words = useDeckWords();
  const money = (cents: number) => formatUSD(cents, { fromCents: true });
  return (
    <>
      <h2 class={styles["s-h"]}>{props.slide.headline}</h2>
      <div
        class={styles["cost"]}
        data-insurance={props.slide.insurance ? "" : undefined}
        data-dense={props.slide.rows.length > DENSE_ROWS ? "" : undefined}
      >
        <table class={styles["cost-t"]}>
          <thead>
            <tr>
              <th scope="col">{words().t("treatment")}</th>
              <th scope="col">{words().t("fee")}</th>
              <Show when={props.slide.insurance}>
                <th scope="col">{words().t("insuranceEstimate")}</th>
                <th scope="col">{words().t("yourPart")}</th>
              </Show>
            </tr>
          </thead>
          <tbody>
            <For each={props.slide.rows}>
              {(row) => (
                <tr>
                  <th scope="row">
                    {row.name}
                    <Show when={row.site}>
                      {(site) => <span class={styles["cost-site"]}>{site()}</span>}
                    </Show>
                  </th>
                  <td>{money(row.feeCents)}</td>
                  <Show when={props.slide.insurance}>
                    <td>{money(row.insuranceCents)}</td>
                    <td>{money(row.patientCents)}</td>
                  </Show>
                </tr>
              )}
            </For>
          </tbody>
          <tfoot>
            <tr>
              <th scope="row">{words().t("total")}</th>
              <td>{money(props.slide.feeCents)}</td>
              <Show when={props.slide.insurance}>
                <td>{money(props.slide.insuranceCents)}</td>
                <td>{money(props.slide.patientCents)}</td>
              </Show>
            </tr>
          </tfoot>
        </table>
        <Show when={props.slide.note}>{(note) => <p class={styles["cost-note"]}>{note()}</p>}</Show>
        <Show when={props.slide.insurance}>
          <p class={styles["cost-fine"]}>
            Insurance amounts are estimates. Your plan decides what it pays.
          </p>
        </Show>
      </div>
    </>
  );
}
